import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  updateCustomerPricing,
  UpdateCustomerPricingPayload,
} from '../../api/queries';
import { PARTNER_CUSTOMERS_QUERY_KEY } from '../queries/useGetPartnerCustomersQuery';
import { PARTNER_CUSTOMER_DETAIL_QUERY_KEY } from '../queries/useGetPartnerCustomerDetailQuery';
import { PARTNER_DASHBOARD_QUERY_KEY } from '../queries/useGetPartnerDashboardQuery';

/**
 * Hook for updating customer pricing information.
 *
 * @example
 * const { mutate } = useUpdateCustomerPricingMutation();
 * mutate({
 *   customerId: 123,
 *   payload: { pricePerSeat: 29900, billingPeriod: 'monthly' }
 * });
 */
export const useUpdateCustomerPricingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customerId,
      payload,
    }: {
      customerId: number;
      payload: UpdateCustomerPricingPayload;
    }): Promise<void> => {
      return updateCustomerPricing(customerId, payload);
    },
    onSuccess: (_data, variables) => {
      // Invalidate customer list (to update cashback calculations)
      queryClient.invalidateQueries({ queryKey: PARTNER_CUSTOMERS_QUERY_KEY });

      // Invalidate specific customer detail
      queryClient.invalidateQueries({
        queryKey: PARTNER_CUSTOMER_DETAIL_QUERY_KEY(variables.customerId)
      });

      // Invalidate dashboard (to update aggregated cashback)
      queryClient.invalidateQueries({ queryKey: PARTNER_DASHBOARD_QUERY_KEY });
    },
  });
};
